// material-ui
import { Button, Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';

// apis
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import { IFreewheelSite, IFreewheelSiteToSiteAssociation } from '~/src/dash/api/freewheel-sites';
// components
import { DeviceTargetingMultiSelect } from '~/src/dash/components/dropdowns/device-targeting-multi-select';
import { SiteTargetingMultiSelect } from '~/src/dash/components/dropdowns/site-targeting-multi-select';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { SettingsAppBar } from '~/src/dash/components/settings-app-bar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
// other
import { extractRouteParams, getRoutePath, RouteChannelExisting } from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import { routeName } from '~/src/dash/utils/routes';
// views
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  freewheelSite: IFreewheelSite;
  onChange: (freewheelSite: IFreewheelSite) => void;
  onSubmit: (freewheelSite: IFreewheelSite) => void;
  onClose: () => void;
  onDelete: () => void;
  onDeleteAsso: (index: number) => void;
}

// NOTE: IState extends INetwork since we will place the network directly into state

interface IState extends IBaseState {
  // error msging to user
  snackbarMessage: string;
  snackbarError: boolean;
  // indicates an api call to update is in progress
  isUpdating: boolean;
  // delete modal bools
  deletePopupActive: boolean;
}

export const FreewheelSettingsForm = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 4,
        paddingLeft: theme.spacing.unit * 4 + 'px !important',
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      addTypography: {
        fontSize: 16,
        fontWeight: 500,
        color: '#5865E8',
        marginLeft: 5,
        paddingBottom: 15,
        cursor: 'pointer',
      },
      paramTitleTypography: {
        marginLeft: 10,
        fontSize: 12,
        fontWeight: 500,
      },
      titleTypography: {
        fontSize: 14,
        fontWeight: 500,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      snackbarMessage: '',
      snackbarError: false,
      isUpdating: false,
      deletePopupActive: false,
    };

    // -------- COMPONENT HELPER FUNCTIONS -------- //
    addFreewheelSiteToSiteAssociation = (): void => {
      const newFreewheelSiteToSiteAssociation = {
        ID: 0,
        SiteID: 0,
        FreewheelSiteID: this.props.freewheelSite.ID,
        TargetingDevices: '0',
        TargetingDealID: '',
      } as IFreewheelSiteToSiteAssociation;
      const freewheelSiteToSiteAssociations = [
        ...this.props.freewheelSite.FreewheelSiteToSiteAssociations,
      ];
      freewheelSiteToSiteAssociations.push(newFreewheelSiteToSiteAssociation);
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelSiteToSiteAssociations: freewheelSiteToSiteAssociations,
      });
    };

    getItemSettingRoute = (channelID: string): string => {
      return getRoutePath(RouteChannelExisting, extractRouteParams(this.props), {
        ch: channelID,
      });
    };

    // -------- HANDLER FUNCTIONS -------- //
    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc.  They are used to not directly modify the props/state
    // and make sure the parent component is properly re-rendered

    // -------- SETTINGS TOP BAR HANDLER FUNCTIONS -------- //

    // Top bar delete
    handleDelete = (): void => {
      this.setState(
        {
          isUpdating: true,
        },
        () => {
          // if this is a new network or cloned network, navigate to list
          if (this.props.freewheelSite.ID === 0) {
            this.props.onClose();
          } else {
            this.props.onDelete();
          }
        },
      );
    };

    handleNameChange = (name: string): void => {
      this.props.onChange({ ...this.props.freewheelSite, Name: name });
    };

    handleFreewheelPublisherIDChange = (ID: string): void => {
      const fwPubID = parseInt(ID, 10);
      if (fwPubID) {
        this.props.onChange({
          ...this.props.freewheelSite,
          FreewheelPublisherID: parseInt(ID, 10),
        });
      } else {
        this.setState({
          snackbarMessage: 'Freewheel Publisher ID must be a positive integer',
          snackbarError: true,
        });
      }
    };

    handleFWPubIDChange = (freewheelPublisherID: number): void => {
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelPublisherID: freewheelPublisherID,
      });
    };

    handleChangeActive = (): void => {
      this.props.onChange({
        ...this.props.freewheelSite,
        Active:
          this.props.freewheelSite.Active === ResourceActiveEnabled
            ? ResourceActiveDisabled
            : ResourceActiveEnabled,
      });
    };

    // -------- SNACKBAR HELPER FUNCTIONS -------- //
    handleSnackbarError = (snackbarError: boolean, snackbarMessage: string): void => {
      this.setState({
        snackbarError,
        snackbarMessage,
      });
    };

    handleSnackbarClose = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // -------- FORM UPDATE FUNCTIONS -------- //

    handleUpdateFWSiteToSiteAsso = (siteIDs: string[], index: number) => {
      const freewheelSiteToSiteAssociations = [
        ...this.props.freewheelSite.FreewheelSiteToSiteAssociations,
      ];
      freewheelSiteToSiteAssociations[index].SiteID = parseInt(siteIDs[0], 10);
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelSiteToSiteAssociations: freewheelSiteToSiteAssociations,
      });
    };

    handleDeleteFWSiteToSiteAsso = (index: number) => {
      this.props.onDeleteAsso(index);
    };

    handleUpdateFWSiteToSiteTargetingDevices = (deviceIDs: string, index: number) => {
      const freewheelSiteToSiteAssociations = [
        ...this.props.freewheelSite.FreewheelSiteToSiteAssociations,
      ];
      freewheelSiteToSiteAssociations[index].TargetingDevices = deviceIDs;
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelSiteToSiteAssociations: freewheelSiteToSiteAssociations,
      });
    };

    handleUpdateFWSiteToSiteTargetingDealID = (dealID: string, index: number) => {
      const freewheelSiteToSiteAssociations = [
        ...this.props.freewheelSite.FreewheelSiteToSiteAssociations,
      ];
      freewheelSiteToSiteAssociations[index].TargetingDealID = dealID;
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelSiteToSiteAssociations: freewheelSiteToSiteAssociations,
      });
    };

    handleUpdateFWSiteToSiteTargetingAppBundleID = (appBundleID: string, index: number) => {
      const freewheelSiteToSiteAssociations = [
        ...this.props.freewheelSite.FreewheelSiteToSiteAssociations,
      ];
      freewheelSiteToSiteAssociations[index].TargetingAppBundleID = appBundleID;
      this.props.onChange({
        ...this.props.freewheelSite,
        FreewheelSiteToSiteAssociations: freewheelSiteToSiteAssociations,
      });
    };

    // -------- RENDER HELPER FUNCTIONS -------- //
    // NOTE: We destructure props and states used in render functions to clearly
    // show what we are passing as well as keeping the code clean and easier to read

    // renders top bar => name edit, save, close, delete
    renderTopBar(): ReactElement {
      const { isUpdating } = this.state;
      const { freewheelSite, onSubmit, onClose, onDelete } = this.props;
      return (
        <Grid item>
          <SettingsAppBar
            name={freewheelSite.Name}
            name2={
              freewheelSite.FreewheelPublisherID === 0
                ? 'Enter FW Pub ID'
                : freewheelSite.FreewheelPublisherID.toString()
            }
            isActive={freewheelSite.Active === ResourceActiveEnabled}
            hasWriteRights={true}
            isUpdating={isUpdating}
            handleNameChange={this.handleNameChange}
            handleNameChange2={this.handleFreewheelPublisherIDChange}
            handleSave={() => onSubmit(freewheelSite)}
            handleClose={onClose}
            handleDelete={onDelete}
            handleChangeActive={this.handleChangeActive}
            relative
          />
        </Grid>
      );
    }

    renderPublicaSites() {
      const { freewheelSite, classes } = this.props;
      return (
        <Grid item style={{ marginTop: 75 }}>
          {freewheelSite.FreewheelSiteToSiteAssociations.map(
            (publicaSiteAsso: IFreewheelSiteToSiteAssociation, i: number) => {
              return (
                <Grid
                  item
                  style={{ width: '100%' }}
                  key={`asso-${publicaSiteAsso.ID}-${publicaSiteAsso.SiteID}-${i}`}
                >
                  <Paper className={classes.paper}>
                    {/* SiteID */}
                    <Grid
                      container
                      direction='column'
                      justify='flex-start'
                      alignItems='flex-start'
                      spacing={2}
                    >
                      <Grid item style={{ width: '100%', marginBottom: 10 }}>
                        <Grid
                          container
                          direction='row'
                          justify='space-between'
                          alignItems='flex-start'
                        >
                          <Grid item>
                            <Button
                              style={{ marginRight: 25 }}
                              component={Link}
                              disabled={publicaSiteAsso.SiteID === 0}
                              to={this.getItemSettingRoute(publicaSiteAsso.SiteID.toString())}
                            >
                              <Grid
                                container
                                direction='column'
                                justify='center'
                                alignItems='flex-start'
                              >
                                <Typography className={classes.titleTypography}>
                                  {publicaSiteAsso.SiteID === 0
                                    ? 'Select a Publica Site'
                                    : `Publica Site ${publicaSiteAsso.SiteID.toString()}`}
                                </Typography>
                              </Grid>
                            </Button>
                          </Grid>
                          <Grid item>
                            <Tooltip title='Delete'>
                              <Button
                                style={{ color: deleteColor }}
                                onClick={() => this.handleDeleteFWSiteToSiteAsso(i)}
                              >
                                <DeleteIcon />
                              </Button>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item style={{ width: '100%' }}>
                        <Grid
                          container
                          direction='row'
                          justify='flex-start'
                          alignItems='center'
                          spacing={1}
                        >
                          <Grid item xs={3}>
                            <Typography className={classes.paramTitleTypography}>
                              Publica Site
                            </Typography>
                          </Grid>
                          <Grid item xs={9} style={{ maxWidth: 450 }}>
                            <SiteTargetingMultiSelect
                              key={`site-targeting-${publicaSiteAsso.ID}-${publicaSiteAsso.SiteID}-${i}`}
                              name='Sites'
                              onChange={(siteIDs: string[]) =>
                                this.handleUpdateFWSiteToSiteAsso(siteIDs, i)
                              }
                              publisherID={this.props.session.publisher.ID}
                              siteIDs={[publicaSiteAsso.SiteID.toString()]}
                              enableMultiSelect={false}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* TargetingDevices */}
                      <Grid item style={{ width: '100%' }}>
                        <Grid
                          container
                          direction='row'
                          justify='flex-start'
                          alignItems='center'
                          spacing={1}
                        >
                          <Grid item xs={3}>
                            <Typography className={classes.paramTitleTypography}>
                              Targeted Devices
                            </Typography>
                          </Grid>
                          <Grid item xs={9} style={{ maxWidth: 450 }}>
                            <DeviceTargetingMultiSelect
                              key={`device-targeting-${publicaSiteAsso.ID}-${publicaSiteAsso.SiteID}-${i}`}
                              name='Devices'
                              onChange={(deviceIDs: string) =>
                                this.handleUpdateFWSiteToSiteTargetingDevices(deviceIDs, i)
                              }
                              publisherID={this.props.session.publisher.ID}
                              deviceIDs={publicaSiteAsso.TargetingDevices}
                              enableMultiSelect={true}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* DealIDs */}
                      <Grid item style={{ width: '100%' }}>
                        <Grid
                          container
                          direction='row'
                          justify='flex-start'
                          alignItems='center'
                          spacing={1}
                        >
                          <Grid item xs={3}>
                            <Typography className={classes.paramTitleTypography}>
                              Targeted Deal IDs
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <FocusedTextfield
                              key={`dealid-targeting-${publicaSiteAsso.ID}-${publicaSiteAsso.SiteID}-${i}`}
                              fullWidth
                              multiline
                              placeholder={'Deal IDs to bid on, comma separated'}
                              label=''
                              name={'DealID'}
                              value={publicaSiteAsso.TargetingDealID}
                              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                                this.handleUpdateFWSiteToSiteTargetingDealID(target.value, i)
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* App Bundle ID*/}
                      <Grid item style={{ width: '100%' }}>
                        <Grid
                          container
                          direction='row'
                          justify='flex-start'
                          alignItems='center'
                          spacing={1}
                        >
                          <Grid item xs={3}>
                            <Typography className={classes.paramTitleTypography}>
                              Targeted App Bundle IDs
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <FocusedTextfield
                              key={`appbundle-id-targeting-${publicaSiteAsso.ID}-${publicaSiteAsso.SiteID}-${i}`}
                              fullWidth
                              multiline
                              placeholder={'App Bundle IDs to bid on, comma separated'}
                              label=''
                              name={'DealID'}
                              value={publicaSiteAsso.TargetingAppBundleID}
                              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                                this.handleUpdateFWSiteToSiteTargetingAppBundleID(target.value, i)
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            },
          )}
        </Grid>
      );
    }

    render() {
      const { snackbarMessage, snackbarError } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel>{this.renderTopBar()}</Panel>
          <Panel scroll largePadding>
            {this.renderPublicaSites()}
            <Grid item>
              <Button onClick={this.addFreewheelSiteToSiteAssociation}>
                <Typography className={classes.addTypography}>+ Add Site</Typography>
              </Button>
            </Grid>
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleSnackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
